import { AlertCircle, Clock, Lock, X } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getSubscriptionInfo, SubscriptionType, isLegacyAccount } from "@/utils/subscriptionUtils";
import { toEpochMillis } from "@graftus/schema";

/** Format a Firestore-style timestamp as a long date in the active UI language. */
const formatDate = (seconds: number, language: string) =>
  new Date(seconds * 1000).toLocaleDateString(language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

/** Format epoch milliseconds as a long date in the active UI language. */
const formatDateMs = (ms: number, language: string) =>
  formatDate(ms / 1000, language);

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * How far ahead of the account-level time limit the warning banner appears.
 * Longer than the 7-day trial warning: these are sales-set pilot and POC end
 * dates, and renewing one means talking to a human rather than clicking a
 * button, so the customer needs more notice.
 */
const ACCESS_ENDING_SOON_DAYS = 14;

export const SubscriptionBanner = () => {
  const { t, i18n } = useTranslation("common");
  const { businessProfile } = useProfile();
  const [isDismissed, setIsDismissed] = useState(false);

  // The account-level time limit outranks every subscription state: when it has
  // passed it is the reason the rest of the app is gone, so it must be shown
  // even for legacy accounts and accounts with no subscription at all.
  const accessValidUntilMs = toEpochMillis(businessProfile?.accessValidUntil);
  if (accessValidUntilMs != null) {
    const msRemaining = accessValidUntilMs - Date.now();

    if (msRemaining < 0) {
      return (
        <div className="bg-gradient-to-r from-red-600 to-red-700 border-b border-red-500/20">
          <div className="max-w-screen-2xl mx-auto px-4 py-2">
            <div className="flex items-center gap-2.5">
              <Lock className="h-4 w-4 text-white flex-shrink-0" />
              <span className="text-sm text-white/95 leading-none">
                {t("subscription.banner.accessEnded", {
                  date: formatDateMs(accessValidUntilMs, i18n.language),
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    const daysRemaining = Math.ceil(msRemaining / MS_PER_DAY);
    if (daysRemaining <= ACCESS_ENDING_SOON_DAYS && !isDismissed) {
      return (
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 border-b border-amber-400/20">
          <div className="max-w-screen-2xl mx-auto px-4 py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                <Clock className="h-4 w-4 text-white flex-shrink-0" />
                <span className="text-sm text-white/95 leading-none">
                  {t("subscription.banner.accessEndingSoon", {
                    count: daysRemaining,
                    date: formatDateMs(accessValidUntilMs, i18n.language),
                  })}
                </span>
              </div>
              <button
                onClick={() => setIsDismissed(true)}
                className="p-1 hover:bg-white/10 rounded transition-colors text-white/80 hover:text-white flex-shrink-0"
                aria-label={t("actions.close")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  if (businessProfile?.accountStatus === "pending_activation") {
    const subscriptionStatus = businessProfile.subscription?.status;
    const hasActiveSubscription =
      subscriptionStatus === "active" || subscriptionStatus === "trialing";

    if (!hasActiveSubscription) {
      return (
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 border-b border-amber-400/20">
          <div className="max-w-screen-2xl mx-auto px-4 py-2">
            <div className="flex items-center gap-2.5">
              <Clock className="h-4 w-4 text-white flex-shrink-0" />
              <span className="text-sm text-white/95 leading-none">
                {t("subscription.banner.pendingActivation")}
              </span>
            </div>
          </div>
        </div>
      );
    }
  }

  // Hide for legacy accounts (pre-Stripe)
  if (isLegacyAccount(businessProfile?.accountType, businessProfile?.subscription)) {
    return null;
  }

  if (!businessProfile?.subscription || isDismissed) {
    return null;
  }

  const subscription = businessProfile.subscription;
  const subscriptionInfo = getSubscriptionInfo(subscription);

  // Determine the message to show based on subscription type
  let message = "";
  let showUpgradeButton = false;
  let isInfoBanner = false; // For active trials (blue instead of purple)

  switch (subscriptionInfo.type) {
    case SubscriptionType.ACTIVE_TRIAL:
      if (subscription.trialEnd) {
        const trialEndDate = new Date(subscription.trialEnd.seconds * 1000);
        const now = new Date();
        const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysRemaining <= 7) {
          // Show banner when trial has 7 days or less
          message = t("subscription.banner.trialEnding", {
            count: daysRemaining,
            date: formatDate(subscription.trialEnd.seconds, i18n.language),
          });
          showUpgradeButton = true;
          isInfoBanner = true;
        }
      }
      break;

    case SubscriptionType.EXPIRED_TRIAL:
      message = t("subscription.banner.expiredTrial");
      showUpgradeButton = true;
      break;

    case SubscriptionType.CANCELED_PAYING:
      message = t("subscription.banner.canceledPaying");
      showUpgradeButton = true;
      break;

    case SubscriptionType.SCHEDULED_CANCELLATION:
      if (subscription.currentPeriodEnd) {
        message = t("subscription.banner.scheduledCancellation", {
          date: formatDate(subscription.currentPeriodEnd.seconds, i18n.language),
        });
        showUpgradeButton = true;
      }
      break;

    case SubscriptionType.INCOMPLETE:
      // Incomplete (including incomplete_expired) - trial ended without payment
      message = t(subscriptionInfo.messageKey);
      showUpgradeButton = true;
      break;

    case SubscriptionType.PAST_DUE:
    case SubscriptionType.UNPAID:
      message = t(subscriptionInfo.messageKey);
      showUpgradeButton = true;
      break;

    default:
      if (subscriptionInfo.needsUpgrade) {
        message = t(subscriptionInfo.messageKey);
        showUpgradeButton = true;
      }
      break;
  }

  if (!message) {
    return null;
  }

  const bannerColors = isInfoBanner
    ? "bg-gradient-to-r from-blue-500 to-blue-600 border-b border-blue-400/20"
    : "bg-gradient-to-r from-brand-gradient-from to-brand-gradient-to border-b border-brand/20";

  const buttonColors = isInfoBanner
    ? "bg-white text-blue-600 hover:bg-blue-50"
    : "bg-white text-brand hover:bg-white/90";

  return (
    <div className={bannerColors}>
      <div className="max-w-screen-2xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <AlertCircle className="h-4 w-4 text-white flex-shrink-0" />
            <span className="text-sm text-white/95 leading-none">{message}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {showUpgradeButton && (
              <Link
                to="/settings/billing"
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors whitespace-nowrap ${buttonColors}`}
              >
                {t(subscriptionInfo.callToActionKey)}
              </Link>
            )}
            <button
              onClick={() => setIsDismissed(true)}
              className="p-1 hover:bg-white/10 rounded transition-colors text-white/80 hover:text-white"
              aria-label={t("actions.close")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
